
import { useState } from 'react'
import { motion } from 'framer-motion'
import { Bell, Menu } from 'lucide-react'
import { useAuth } from '@/context/auth-context'
import { CounsellorSidebar } from '@/components/counsellor/counsellor-sidebar'
import { CounsellorLeadsView } from '@/components/counsellor/counsellor-leads-view'
import { CounsellorHomeView } from '@/components/counsellor/counsellor-home-view'
import { CounsellorAdmissionTrackerView } from '@/components/counsellor/counsellor-admission-tracker-view'
import { useLocation, Link } from 'react-router-dom'

// Mobile Sidebar
function MobileSidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    if (!isOpen) return null
    return (
        <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="fixed inset-y-0 left-0 w-64 bg-slate-900 shadow-xl transform transition-transform duration-300">
                <CounsellorSidebar />
            </div>
        </div>
    )
}

export function CounsellorDashboardPage() {
    const { user } = useAuth()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const activeView = searchParams.get('view') || 'dashboard'

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex">
            {/* Desktop Sidebar */}
            <div className="hidden lg:block w-64 fixed h-full">
                <CounsellorSidebar />
            </div>

            {/* Mobile Sidebar */}
            <MobileSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            {/* Main Content */}
            <div className="flex-1 lg:ml-64 min-h-screen flex flex-col">
                {/* Header */}
                <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden p-2 -ml-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                        >
                            <Menu className="h-6 w-6 text-theme-primary" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-theme-primary">Dashboard</h1>
                            <p className="text-xs text-theme-secondary hidden sm:block">Welcome back, {user?.name}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <button className="p-2 text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors relative">
                            <Bell className="h-5 w-5" />
                            <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full border-2 border-white dark:border-slate-900"></span>
                        </button>
                        <Link to="/counsellor/profile" className="flex items-center gap-3 pl-4 border-l border-slate-200 dark:border-slate-700 hover:opacity-80 transition-opacity">
                            <div className="h-8 w-8 bg-primary-blue/10 rounded-full flex items-center justify-center text-primary-blue font-bold text-sm">
                                {user?.avatar}
                            </div>
                            <span className="text-sm font-medium text-theme-primary hidden md:block">{user?.name}</span>
                        </Link>
                    </div>
                </header>

                {/* Dashboard Content */}
                <main className="flex-1 p-6 relative">
                    <div className="max-w-7xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            {activeView === 'leads' ? (
                                <CounsellorLeadsView />
                            ) : activeView === 'admissions' ? (
                                <CounsellorAdmissionTrackerView />
                            ) : (
                                <CounsellorHomeView />
                            )}
                        </motion.div>
                    </div>
                </main>
            </div>
        </div>
    )
}
